import React, { useMemo, useState } from "react";
import { AnyObject, Form, FormSpy } from "react-final-form";
import { useSelector } from "react-redux";
import { AddBox as AddBoxIcon } from "@mui/icons-material";
import { Box, Button, FormHelperText, Grid, SelectChangeEvent, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import classNames from "classnames";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import { selectVersionsByProjectId, useCreateExportMutation, useGetAccountModulesQuery, useGetLayersQuery, useGetVersionStatusQuery } from "fond/api";
import { CheckboxField } from "fond/form/fields";
import { useCurrentProject } from "fond/hooks/useCurrentProject";
import { useGetScenarioConfiguration } from "fond/hooks/useGetScenarioConfiguration";
import useVersionHasScheduleOfRates from "fond/hooks/useVersionHasScheduleOfRates";
import { LayerIds } from "fond/layers";
import { ExportFormats, getExportFormat, SplitByTier, Store, Version } from "fond/types";
import { accountModuleCheck, Actions } from "fond/utils/permissions";
import {
  isDesignProject,
  isExtendedPlannerProject,
  isNBNProject,
  isPlannerProject,
  supportsConstructionPrintExport,
  useIsDesignProjectComplete,
} from "fond/utils/project";
import { required } from "fond/utils/validation";
import { Message as MessageWidget, Modal } from "fond/widgets";

import LayerTree from "../LayerTree";

import {
  BoldText,
  ExportForm,
  FormGridContainer,
  GreyText,
  LayerBox,
  LayerGridItem,
  LayerTableContainer,
  ModalContent,
  StyledNotificationImportantIcon,
  StyledSelectField,
  StyledTextField,
} from "./NewExportModal.styles";

const useCustomStyles = makeStyles((theme: Theme) => ({
  selectLabel: {
    fontSize: theme.typography.size.normal,
    marginBottom: 4,
  },
  textLabel: {
    marginBottom: 4,
  },
}));

interface IFormData {
  bom: boolean;
  comments: boolean;
  description: string;
  fiberCableSplit: boolean;
  format: ExportFormats;
  hubSplit: boolean;
  layers: string[];
  name: string;
  spliceSplit: boolean;
  spliceTables: boolean;
  constructionPrints: boolean;
  unitOfMeasurement: string;
  version: string;
  scheduleOfRates: boolean;
}

interface NewExportModalProps {
  /**
   * Set the component test identifier.
   */
  "data-testid"?: string;
  /**
   * Used in unit tests to set the select field to native.
   */
  nativeSelect?: boolean;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose(): void;
  /**
   * Callback function to be executed after an new export is register.
   */
  onPostExport?(): void;
}

const NewExportModal: React.FC<NewExportModalProps> = ({
  "data-testid": dataTestid = "new-export-modal",
  nativeSelect = false,
  onClose,
  onPostExport,
}: NewExportModalProps) => {
  const classes = useCustomStyles();
  const { enqueueSnackbar } = useSnackbar();

  const commentsPresent = useSelector((state: Store): boolean => (state.comments.items ? Object.keys(state.comments.items).length > 0 : false));

  const project = useCurrentProject();
  const isProjectWithDesign = isPlannerProject(project) || isDesignProject(project);

  const { data: accountModules } = useGetAccountModulesQuery(project?.Account.ID ?? skipToken);

  const isNbnProjectFlag = isNBNProject(project);

  const versions = useSelector((state: Store): Version[] => (project && selectVersionsByProjectId(state, project.ID)) || []);
  const versionOptions = versions && [...versions].map((item) => ({ value: item.ID, displayValue: item.Name }));

  // Only show version select when the project has multiple versions
  const showVersionSelect = versions.length > 1;

  const [selectedVersion, setSelectedVersion] = useState<Version | undefined>(showVersionSelect ? undefined : versions[0]);

  // Version status only required for planner projects
  const { data: status } = useGetVersionStatusQuery(selectedVersion?.ID ?? skipToken, {
    skip: !isProjectWithDesign,
  });
  const hasSolution = status?.HasSolution;

  // Splice tables can always be exported on 'planner_extended' projects; in all other cases the account
  // must have the splice tables module enabled.
  const canExportSpliceTables: boolean =
    isExtendedPlannerProject(project) || accountModuleCheck(accountModules, Actions.PROJECT_EXPORT_SPLICE_TABLES);
  // Only account with parcel export module can export parcel layer (unless a collaboration project).
  const canExportParcels: boolean = accountModuleCheck(accountModules, Actions.PARCEL_EXPORT);
  // Only account with address export module can export address layer (unless a collaboration project).
  // Note: The address layer is called "demand".
  const canExportAddresses: boolean = accountModuleCheck(accountModules, Actions.ADDRESS_EXPORT);

  const canExportConstructionPrints: boolean = supportsConstructionPrintExport(project);

  const isDesignProjectComplete = useIsDesignProjectComplete({ versionId: selectedVersion?.ID, project, rootVersionId: versions.at(-1)?.ID });

  // The BOM can be exported on any version of a collaboration cost-to-serve project.
  const { hasConfig: isCollaborationCostToServe } = useGetScenarioConfiguration({ projectId: project?.ID });
  const bomExportVisible = isProjectWithDesign || isCollaborationCostToServe;
  const bomExportAvailable = hasSolution || (isNbnProjectFlag && isDesignProjectComplete) || isCollaborationCostToServe;

  // The Schedule of Rates can be exported for NBN projects with a design solution, as well as collaboration cost-to-serve
  // projects with a solution.
  const { possible: scheduleOfRatesExportVisible, available: scheduleOfRatesExportAvailable } = useVersionHasScheduleOfRates({
    project,
    versionId: selectedVersion?.ID,
  });

  useGetLayersQuery(selectedVersion?.ID || "", {
    skip: !selectedVersion?.ID,
    refetchOnMountOrArgChange: true,
  });

  // A list of Layer Keys that should be excluded from the export.
  const excludedKeys = useMemo(() => {
    const keys = ["projectComments"];
    if (!canExportAddresses && isProjectWithDesign) keys.push("input_layers/demand");
    if (!canExportParcels && isProjectWithDesign) keys.push("input_layers/parcel");

    return keys;
  }, [canExportAddresses, canExportParcels, isProjectWithDesign]);

  const [selectedLayers, setSelectedLayers] = useState<string[]>([]);
  const [createExport, { isLoading: isSaving, isError }] = useCreateExportMutation();
  const [errorMsg, setErrorMsg] = useState<null | string>(null);

  const exportFormatOptions = ExportFormats.map((format) => {
    return {
      value: format,
      displayValue: getExportFormat(format),
    };
  });

  let submit: (event?: Partial<Pick<React.SyntheticEvent, "preventDefault" | "stopPropagation">>) => Promise<AnyObject | undefined> | undefined;

  const handleOnClick = (event: React.MouseEvent<EventTarget>) => {
    // Submits the React-Final-Form (which handles validation & calling onSubmit if validation passes)
    submit();
  };

  const onSubmit = async (values: IFormData) => {
    const {
      name,
      version,
      format,
      description,
      bom,
      spliceTables,
      fiberCableSplit,
      hubSplit,
      spliceSplit,
      comments,
      scheduleOfRates,
      constructionPrints,
    } = values;

    if (
      selectedLayers.length === 0 &&
      !values.bom &&
      !values.comments &&
      !values.fiberCableSplit &&
      !values.hubSplit &&
      !values.scheduleOfRates &&
      !values.spliceTables &&
      !values.spliceSplit
    ) {
      setErrorMsg("Please select at least one element to export.");
    } else {
      const params = {
        VersionID: version || versions[0].ID,
        Name: name,
        Format: format,
        Description: description,
        BOM: bom,
        SystemOfMeasurement: project.SystemOfMeasurement,
        SpliceTables: spliceTables,
        Comments: comments,
        FiberCableSplit: fiberCableSplit && selectedLayers.includes(LayerIds.fibreCable),
        HubSplit: hubSplit && selectedLayers.includes(LayerIds.hub),
        SpliceSplit: spliceSplit && selectedLayers.includes(LayerIds.splice),
        Layers: selectedLayers,
        ScheduleOfRates: scheduleOfRates,
        ConstructionPrints: canExportConstructionPrints && constructionPrints ? { Type: "standard" as const } : null,
      };

      createExport(params)
        .unwrap()
        .then((response) => {
          onClose();
          if (onPostExport) {
            onPostExport();
          }
          analytics.track("Created export", {
            projectId: project.ID,
            accountId: project.Account.ID,
            bom,
            exportId: response.ID,
            format,
            fiberCableSplit,
            hubSplit,
            spliceSplit,
            spliceTables,
            comments,
            scheduleOfRates,
            constructionPrints: !!params.ConstructionPrints,
          });
          enqueueSnackbar(`Created export '${values.name}'.`, { variant: "success" });
        });
    }
  };

  const handleVersionSelect = (event: SelectChangeEvent) => {
    if (event.target.value) {
      const newVersionId = event.target.value;
      setSelectedVersion(versions.find((item) => item.ID === newVersionId));
      setSelectedLayers([]);
    }
  };

  return (
    <Form<IFormData>
      initialValues={{
        format: "shp",
      }}
      onSubmit={onSubmit}
      render={({ handleSubmit }) => {
        submit = handleSubmit;
        return (
          <Modal
            open
            onClose={onClose}
            header="New Export"
            headerIcon={<AddBoxIcon />}
            data-testid={dataTestid}
            showFullscreen
            showClose
            variant="primary"
            DialogContentProps={{ style: { display: "flex", flexDirection: "column", minHeight: 0 } }}
            content={
              <ModalContent>
                {isError && (
                  <Box mb={2} data-testid="export-error">
                    <MessageWidget type="error">There was an issue creating the export. Please try again.</MessageWidget>
                  </Box>
                )}

                <ExportForm onSubmit={handleSubmit}>
                  <FormGridContainer container display="flex" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Grid item xs={6}>
                      {showVersionSelect && (
                        <StyledSelectField
                          name="version"
                          labelId="version"
                          label="Select Version"
                          data-testid="export-version-select"
                          required
                          hideError
                          validate={required}
                          sxProps={{ minWidth: 160 }}
                          nativeSelect={nativeSelect}
                          options={versionOptions}
                          labelClasses={{ root: classes.selectLabel }}
                          onCustomChange={handleVersionSelect}
                        />
                      )}
                      <Box pt={2}>
                        <Grid container display="flex" alignItems="center" justifyContent="space-between" spacing={2}>
                          <Grid item xs={6}>
                            <StyledTextField
                              name="name"
                              inputProps={{ "data-testid": "export-name" }}
                              required
                              hideError
                              fullWidth
                              label="Name"
                              margin="dense"
                              validate={required}
                              labelClasses={{ root: classes.textLabel }}
                              placeholder="Export Name"
                              sx={{ marigin: 0 }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <StyledSelectField
                              name="format"
                              data-testid="export-file-format"
                              labelId="export-format"
                              label="Layer File Format"
                              fullWidth
                              required
                              hideError
                              validate={required}
                              nativeSelect={nativeSelect}
                              options={exportFormatOptions}
                              labelClasses={{ root: classes.selectLabel }}
                              menuStyle={{ fontSize: 14 }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box pt={2}>
                        <StyledTextField
                          inputProps={{ "data-testid": "export-description" }}
                          labelClasses={{ root: classes.textLabel }}
                          style={{ marginTop: 0 }}
                          name="description"
                          label="Description (Optional)"
                          placeholder="Enter description"
                        />
                      </Box>
                      {!hasSolution && isProjectWithDesign && !(isNbnProjectFlag && isDesignProjectComplete) && (
                        <Box display="flex" alignItems="center" pt={2}>
                          <StyledNotificationImportantIcon />
                          <GreyText variant="caption">You need to generate a design first to enable the below options</GreyText>
                        </Box>
                      )}
                      <Box pt={2}>
                        {isPlannerProject(project) && (
                          <>
                            <Box display="flex" alignItems="center">
                              <BoldText variant="body2" display="block">
                                Separate by tier
                              </BoldText>
                            </Box>
                            <Box display="flex">
                              <Box>
                                <CheckboxField
                                  dataTestId="split-tier-fiber"
                                  sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                                  name={SplitByTier[LayerIds.fibreCable]}
                                  size="small"
                                  color="primary"
                                  label="Fiber"
                                  disabled={!selectedLayers.includes(LayerIds.fibreCable)}
                                />
                              </Box>
                              <Box>
                                <CheckboxField
                                  dataTestId="split-tier-hub"
                                  sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                                  name={SplitByTier[LayerIds.hub]}
                                  size="small"
                                  color="primary"
                                  label="Hub"
                                  disabled={!selectedLayers.includes(LayerIds.hub)}
                                />
                              </Box>
                              <Box>
                                <CheckboxField
                                  dataTestId="split-tier-splice"
                                  sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                                  name={SplitByTier[LayerIds.splice]}
                                  size="small"
                                  color="primary"
                                  label="Splice"
                                  disabled={!selectedLayers.includes(LayerIds.splice)}
                                />
                              </Box>
                            </Box>
                          </>
                        )}

                        <Box display="flex" alignItems="center" pt={1}>
                          <BoldText variant="body2" display="block">
                            Add to Export
                          </BoldText>
                        </Box>
                        <Box display="flex" flexWrap="wrap" data-testid="export-add-options">
                          {bomExportVisible && (
                            <Box width="50%">
                              <CheckboxField
                                dataTestId="export-add-bom"
                                sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                                name="bom"
                                size="small"
                                color="primary"
                                label="Material & Costs"
                                disabled={!bomExportAvailable}
                              />
                            </Box>
                          )}
                          {isProjectWithDesign && (
                            <Box width="50%">
                              {canExportSpliceTables && (
                                <CheckboxField
                                  dataTestId="export-add-splice-tables"
                                  sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                                  name="spliceTables"
                                  size="small"
                                  color="primary"
                                  label="Splice Tables"
                                  disabled={!hasSolution}
                                />
                              )}
                            </Box>
                          )}
                          <Box width="50%">
                            <CheckboxField
                              dataTestId="export-comments"
                              sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                              name="comments"
                              size="small"
                              color="primary"
                              label="Comments"
                              disabled={!commentsPresent}
                            />
                          </Box>
                          {canExportConstructionPrints && (
                            <Box width="50%">
                              <CheckboxField
                                dataTestId="export-construction-prints"
                                sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                                name="constructionPrints"
                                size="small"
                                color="primary"
                                label="Construction Prints"
                              />
                            </Box>
                          )}
                        </Box>
                        {scheduleOfRatesExportVisible && (
                          <Box display="flex">
                            <Box>
                              <CheckboxField
                                data-testid="export-schedule-of-rates-acum"
                                sxProps={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                                name="scheduleOfRates"
                                size="small"
                                color="primary"
                                label="Schedule of Rates"
                                disabled={!scheduleOfRatesExportAvailable}
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <LayerGridItem item xs={6} pt={1}>
                      <LayerBox>
                        <LayerTableContainer className={classNames("customScrollbars")}>
                          <LayerTree
                            versionId={selectedVersion?.ID}
                            onSelectionChange={setSelectedLayers}
                            excludedKeys={excludedKeys}
                            title={selectedVersion?.Name}
                          />
                        </LayerTableContainer>
                      </LayerBox>
                    </LayerGridItem>
                  </FormGridContainer>
                  {errorMsg && (
                    <FormHelperText data-testid="export-layer-select-error" error>
                      {errorMsg}
                    </FormHelperText>
                  )}
                </ExportForm>
              </ModalContent>
            }
            actions={
              <>
                <Button data-testid="cancel-button" onClick={onClose}>
                  Cancel
                </Button>
                <FormSpy<IFormData> subscription={{ values: true, dirty: true }}>
                  {({ values }) => (
                    <LoadingButton
                      data-testid="export-button"
                      variant="contained"
                      color="primary"
                      onClick={handleOnClick}
                      loading={isSaving}
                      disabled={selectedLayers.length === 0 && !values.comments}
                    >
                      Export
                    </LoadingButton>
                  )}
                </FormSpy>
              </>
            }
          />
        );
      }}
    />
  );
};

export default NewExportModal;
